import { useMutation, useQueryClient } from '@tanstack/react-query';

import { uploadWorkspaceFile } from '@/lib/api/generated/api';
import { expectStatus } from '@/lib/api/client';
import type { FileChangeEventView } from '@/lib/api/generated/model';
import { getUploadFailureMessage } from '../errors';
import { fileKeys } from '../queryKeys';

/**
 * Uploads one file to a target path within the workspace. `UploadControls`
 * calls this once per file in a batch (including every file produced by
 * walking a dropped/selected directory) so one file's failure never blocks
 * or rolls back any other (FR-010).
 *
 * Invalidates the *currently-viewed* folder's listing rather than the
 * uploaded file's own parent — a dropped directory can place files several
 * levels deeper than `currentPath`, but only the listing on screen needs to
 * refresh immediately; a not-yet-cached nested listing simply fetches fresh
 * the first time the user navigates into it.
 */
export function useUploadWorkspaceFile(
  workspaceId: string,
  currentPath: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      path,
      file,
    }: {
      path: string;
      file: File;
    }): Promise<FileChangeEventView> =>
      expectStatus(
        await uploadWorkspaceFile(workspaceId, { path, file }),
        200,
        getUploadFailureMessage(file.name),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: fileKeys.listing(workspaceId, currentPath),
      }),
  });
}
